/**
 * Shared Axios instance with automatic Bearer token injection
 * and 401 response handling (logout + redirect to login).
 *
 * All service modules should import `apiClient` instead of raw `axios`
 * so that every request carries the current JWT token.
 */
import axios from 'axios';
import { store } from '../store';
import { logout } from '../store/authSlice';

const apiClient = axios.create();

apiClient.interceptors.request.use((config) => {
  const token = store.getState().auth.token;
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add cache-busting headers to prevent stale data
  config.headers = config.headers || {};
  config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  config.headers['Pragma'] = 'no-cache';
  config.headers['Expires'] = '0';

  // Attach correlation ID for backend observability linking
  const correlationId = (window as unknown as { __correlationId?: string }).__correlationId;
  if (correlationId) {
    config.headers['X-Correlation-ID'] = correlationId;
  }
  
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    // Capture correlation ID from response headers for observability linking
    const cid = response.headers?.['X-Correlation-ID'] || response.headers?.['x-correlation-id'];
    if (cid) {
      (window as unknown as { __correlationId?: string }).__correlationId = cid;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      // Redirect to login unless already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
